// Keyword search over bridge products: accent-insensitive, typo-tolerant,
// synonym-aware. Any keyword may match; products matching more keywords rank first.
export const normalizeText = (s) =>
    String(s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const STOPWORDS = new Set([
    "de", "del", "la", "las", "el", "los", "lo", "un", "una", "unos", "unas", "para", "por", "con", "sin",
    "y", "o", "e", "en", "al", "a", "que", "mi", "me", "quiero", "busco", "buscando", "algo", "tipo",
    "the", "for", "and", "of", "with",
]);

// Light plural stemming: zapatos→zapato, pantalones→pantalon, negros→negro.
const stem = (w) => {
    if (w.length > 5 && w.endsWith("es") && !/[aeiou]es$/.test(w.slice(-3))) return w.slice(0, -2);
    if (w.length > 3 && w.endsWith("s")) return w.slice(0, -1);
    return w;
};

const SYNONYM_GROUPS = [
    ["zapato", "calzado", "tenis", "sandalia", "bota", "tacon", "zapatilla", "sneaker", "shoe", "mocasin"],
    ["hombre", "caballero", "masculino", "varon", "men", "man"],
    ["mujer", "dama", "femenino", "senora", "women", "woman"],
    ["nina", "nena"],
    ["nino", "nene"],
    ["bebe", "baby"],
    ["perfume", "fragancia", "colonia", "locion", "aroma", "spray"],
    ["pantalon", "jean", "pant", "jogger"],
    ["camisa", "blusa", "polo", "camiseta", "playera", "shirt"],
    ["vestido", "dress"],
    ["bolso", "cartera", "bolsa"],
    ["sueter", "sudadera", "sweater", "hoodie", "abrigo", "chaqueta"],
    ["bano", "bikini"],
    ["brasier", "bracier", "sosten"],
    ["licra", "legging", "leggin"],
    ["joya", "joyeria", "collar", "arete", "pulsera", "anillo"],
];
const SYNONYMS = new Map();
SYNONYM_GROUPS.forEach((raw) => {
    const g = raw.map(stem);
    g.forEach((w) => SYNONYMS.set(w, g.filter((x) => x !== w)));
});

const tokenize = (s) => normalizeText(s).split(/[\s-]+/).filter(Boolean).map(stem);

const editDistance = (a, b, max) => {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const cur = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j++) {
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            rowMin = Math.min(rowMin, cur[j]);
        }
        if (rowMin > max) return max + 1;
        prev = cur;
    }
    return prev[b.length];
};

const allowedTypos = (len) => (len >= 8 ? 2 : len >= 5 ? 1 : 0);

// How well one keyword matches a list of words (0..1).
const tokenScore = (qt, words) => {
    let best = 0;
    const max = allowedTypos(qt.length);
    for (const w of words) {
        if (w === qt) return 1;
        if (qt.length >= 2 && w.startsWith(qt)) best = Math.max(best, 0.85);
        else if (qt.length >= 4 && w.includes(qt)) best = Math.max(best, 0.6);
        else if (max > 0 && w[0] === qt[0]) {
            const targets = [w, w.slice(0, qt.length), w.slice(0, qt.length + 1)];
            if (targets.some((t) => editDistance(qt, t, max) <= max)) best = Math.max(best, 0.5);
        }
    }
    return best;
};

const FIELDS = [
    { key: "nombre", weight: 10 },
    { key: "categoria", weight: 8 },
    { key: "descripcion", weight: 3 },
];

const wordCache = new WeakMap();
const productWords = (p) => {
    let w = wordCache.get(p);
    if (!w || w.src !== p.nombre + p.categoria + p.descripcion) {
        w = {
            src: p.nombre + p.categoria + p.descripcion,
            fields: FIELDS.map((f) => ({ weight: f.weight, words: tokenize(p[f.key]) })),
        };
        wordCache.set(p, w);
    }
    return w.fields;
};

// Split a query into keywords (stopwords dropped unless that leaves nothing).
const queryKeywords = (query) => {
    const all = tokenize(query);
    const kept = all.filter((t) => !STOPWORDS.has(t));
    return [...new Set(kept.length ? kept : all)];
};

const keywordScore = (kw, fields) => {
    let best = 0;
    for (const f of fields) best = Math.max(best, tokenScore(kw, f.words) * f.weight);
    for (const syn of SYNONYMS.get(kw) || []) {
        for (const f of fields) {
            if (f.words.some((w) => w === syn || (syn.length >= 4 && w.startsWith(syn)))) best = Math.max(best, 0.8 * f.weight);
        }
    }
    return best;
};

// Relevance score (> 0 means match). Matching more keywords always ranks higher.
export const scoreProduct = (p, query, keywords = queryKeywords(query)) => {
    const q = normalizeText(query);
    if (!q) return 1;
    const id = normalizeText(p.id || p.sku).replace(/[\s-]/g, "");
    const qCompact = q.replace(/[\s-]/g, "");
    if (id && id === qCompact) return 100000;
    const fields = productWords(p);
    let matched = 0;
    let total = 0;
    keywords.forEach((kw, idx) => {
        let s = keywordScore(kw, fields);
        if (id && kw.length >= 3 && id.includes(kw.replace(/-/g, ""))) s = Math.max(s, 12);
        // Earlier keywords usually name the product type ("zapatos" in "zapatos para hombre").
        if (s > 0) { matched += 1; total += s * (1 + (0.5 * (keywords.length - 1 - idx)) / keywords.length); }
    });
    if (!matched) return 0;
    const name = normalizeText(p.nombre);
    if (name.includes(q)) total += 15;
    return matched * 100 + total;
};

export const searchProducts = (products, query) => {
    if (!normalizeText(query)) return products;
    const keywords = queryKeywords(query);
    return products
        .map((p, i) => ({ p, i, s: scoreProduct(p, query, keywords) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s || a.i - b.i)
        .map((x) => x.p);
};
